import React, { useEffect } from 'react';
import cn from 'classnames';
import { EditorMode } from 'types/story';
import { convertPxToVw } from 'utils/helpers';
import { FIELD_ERROR_DATA_ATTR } from '../constants';
import { ErrorEditable } from './ErrorEditable';
import css from './Error.scss';

type ErrorProps = {
	isEditableMode: boolean;
	text: string;
	errorType?: string;
	path?: string;
	isHidden: boolean;
	editorMode: EditorMode;
};

type ErrorNodeProps = {
	ref: React.RefObject<HTMLParagraphElement>;
	className: string;
	[FIELD_ERROR_DATA_ATTR]: string;
};

const useMarginUpdate = (nodeRef: React.RefObject<HTMLParagraphElement>) => {
	const updateMargin = React.useCallback(() => {
		const node = nodeRef.current;
		if (!node) return;

		const parent = node.parentNode;
		const nodeHeight = window.getComputedStyle(node).height;
		const marginInVw = convertPxToVw(nodeHeight);

		if (parent) {
			(parent as HTMLElement).style.marginBlockEnd = parseInt(marginInVw, 10) === 0 ? '' : marginInVw;
		}
	}, [nodeRef]);

	useEffect(() => {
		const node = nodeRef.current;
		if (!node) return () => {};

		// Handle element size changes
		const resizeObserver = new ResizeObserver(updateMargin);
		resizeObserver.observe(node);

		// Initial update
		updateMargin();

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updateMargin);

			const parent = node.parentNode as HTMLElement;
			if (parent) {
				parent.style.marginBlockEnd = '';
			}
		};
	}, [updateMargin, nodeRef]);
};

export const ErrorMessage: React.FC<ErrorProps> = ({
	path = '',
	errorType,
	isEditableMode,
	isHidden,
	text,
	editorMode,
}) => {
	const nodeRef = React.useRef<HTMLParagraphElement>(null);
	useMarginUpdate(nodeRef);

	const isEditableError = errorType !== undefined && isEditableMode;
	const nodeProps: ErrorNodeProps = {
		ref: nodeRef,
		className: cn(css.error, { [css.hidden]: isHidden }),
		[FIELD_ERROR_DATA_ATTR]: '',
	};

	if (isEditableError) {
		return (
			<div {...nodeProps}>
				<ErrorEditable path={path} errorType={errorType} text={text} editorMode={editorMode} />
			</div>
		);
	}

	// eslint-disable-next-line react/no-danger
	return <div {...nodeProps} dangerouslySetInnerHTML={{ __html: text }} />;
};
